import React, { useEffect } from 'react';
import { Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
	const navigate = useNavigate();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const plans = [
		{
			id: 'starter',
			name: 'Starter Pack',
			tagline: 'Best for clinics, boutique cafes & single billing desks',
			price: '₹999',
			originalPrice: '₹1,999',
			discount: '50% OFF',
			quantityBadge: '3 NFC Cards',
			isPopular: false,
			features: [
				'3x Premium NFC Contactless PVC Cards',
				'Dynamic High-Contrast QR Code on back',
				'Built-in AI 5-Star Review Generator',
				'Negative Review Shield (1-3★ routed privately)',
				'Instant Tap on iOS & Android (Zero Apps)',
				'Private Dashboard for reviews & feedback',
				'Unlimited customer taps & QR scans for life',
				'Free Pan-India Express Delivery',
			],
			support: 'WhatsApp & Email Support',
			ctaText: 'Order Starter Pack',
			actionUrl: '/signup?plan=starter',
		},
		{
			id: 'economy',
			name: 'Economy Pack',
			tagline: 'Most chosen by busy restaurants, clinics & salons',
			price: '₹1,499',
			originalPrice: '₹2,999',
			discount: '50% OFF',
			quantityBadge: '6 NFC Cards',
			isPopular: true,
			badgeText: '🔥 Most Popular Choice',
			features: [
				'6x Premium NFC Contactless PVC Cards',
				'Dynamic High-Contrast QR Code on back',
				'Custom Business Branding & Logo Placement',
				'AI Review Generator with custom tone',
				'Negative Review Shield (Auto grievance routing)',
				'Deploy across multiple dining tables or billing counters',
				'Private Analytics & Live Sentiment Portal',
				'Unlimited customer taps & QR scans for life',
				'Free Priority Express Shipping Pan-India',
			],
			support: 'Priority WhatsApp & Dedicated Phone Onboarding',
			ctaText: 'Order Economy Pack',
			actionUrl: '/signup?plan=economy',
		},
		{
			id: 'prime',
			name: 'Prime Enterprise',
			tagline: 'For multi-floor stores, hotel chains & franchises',
			price: '₹1,999',
			originalPrice: '₹3,999',
			discount: '50% OFF',
			quantityBadge: '9 NFC Cards',
			isPopular: false,
			features: [
				'9x Premium NFC Contactless PVC Cards',
				'Dynamic High-Contrast QR Code on back',
				'Multi-location & Multi-branch review routing',
				'Advanced AI prompt engineering for your industry',
				'Negative Review Shield with instant alerts',
				'Staff & Counter conversion performance tracking',
				'Lifetime Warranty against chip & physical defects',
				'Free Priority Express Courier Dispatch',
			],
			support: '1-on-1 VIP Setup via Scheduled Google Meet',
			ctaText: 'Order Prime Pack',
			actionUrl: '/signup?plan=prime',
		},
	];

	return (
		<section id='pricing' className='py-20 bg-white border-t border-slate-200'>
			<Container maxWidth='lg'>
				<div className='text-center max-w-3xl mx-auto mb-16'>
					<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 mb-3'>
						One-Time Investment • Zero Monthly Fees
					</span>
					<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
						Transparent Pricing For Every Business
					</h2>
					<p className='text-slate-600 text-base sm:text-lg mt-3 leading-relaxed'>
						Get your smart review cards delivered straight to your doorstep anywhere in India.
					</p>

					{/* Trust highlights */}
					<div className='mt-6 inline-flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-slate-600 bg-slate-50 py-2.5 px-6 rounded-full border border-slate-200'>
						<span className='flex items-center gap-1.5 font-medium'>
							<span className='text-emerald-600 font-bold'>✓</span> Lifetime Card Validity
						</span>
						<span className='flex items-center gap-1.5 font-medium'>
							<span className='text-emerald-600 font-bold'>✓</span> Free Pan-India Courier
						</span>
						<span className='flex items-center gap-1.5 font-medium'>
							<span className='text-emerald-600 font-bold'>✓</span> 100% Replacement Warranty
						</span>
					</div>
				</div>

				<div className='grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch'>
					{plans.map((plan) => (
						<div
							key={plan.id}
							className={`relative rounded-3xl p-8 flex flex-col justify-between transition-all duration-200 ${
								plan.isPopular
									? 'bg-slate-900 text-white shadow-xl ring-2 ring-blue-600 transform lg:-translate-y-2'
									: 'bg-white border border-slate-200 text-slate-900 shadow-sm hover:shadow-md'
							}`}>
							{/* Popular Badge */}
							{plan.isPopular && (
								<div className='absolute -top-3.5 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider shadow'>
									{plan.badgeText}
								</div>
							)}

							<div>
								{/* Header */}
								<div className='flex items-center justify-between'>
									<h3 className={`text-2xl font-bold ${plan.isPopular ? 'text-white' : 'text-slate-900'}`}>
										{plan.name}
									</h3>
									<span
										className={`px-3 py-1 rounded-full text-xs font-bold ${
											plan.isPopular
												? 'bg-white/10 text-blue-300 border border-white/10'
												: 'bg-blue-50 text-blue-700 border border-blue-200'
										}`}>
										{plan.quantityBadge}
									</span>
								</div>

								<p className={`text-xs mt-2 leading-relaxed ${plan.isPopular ? 'text-slate-300' : 'text-slate-500'}`}>
									{plan.tagline}
								</p>

								{/* Price */}
								<div className={`mt-6 pb-6 border-b flex items-baseline gap-3 ${plan.isPopular ? 'border-slate-800' : 'border-slate-100'}`}>
									<span className={`text-4xl sm:text-5xl font-extrabold tracking-tight ${plan.isPopular ? 'text-white' : 'text-slate-900'}`}>
										{plan.price}
									</span>
									<span className='text-base line-through text-slate-400'>
										{plan.originalPrice}
									</span>
									<span className='text-xs font-bold px-2 py-0.5 rounded bg-emerald-500 text-white'>
										{plan.discount}
									</span>
									<span className={`text-xs ml-auto font-medium ${plan.isPopular ? 'text-slate-400' : 'text-slate-500'}`}>
										One-Time Cost
									</span>
								</div>

								{/* Features list */}
								<div className='mt-6 space-y-3'>
									<p className={`text-[11px] font-bold uppercase tracking-wider ${plan.isPopular ? 'text-slate-400' : 'text-slate-500'}`}>
										What's Included:
									</p>
									{plan.features.map((feature, idx) => (
										<div key={idx} className='flex items-start gap-2.5 text-xs sm:text-sm'>
											<span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5 font-bold ${
												plan.isPopular ? 'bg-blue-500/20 text-blue-300' : 'bg-emerald-100 text-emerald-700'
											}`}>
												✓
											</span>
											<span className={plan.isPopular ? 'text-slate-200' : 'text-slate-600'}>
												{feature}
											</span>
										</div>
									))}
								</div>
							</div>

							{/* Support & CTA */}
							<div className={`mt-8 pt-6 border-t ${plan.isPopular ? 'border-slate-800' : 'border-slate-100'}`}>
								<div className={`text-xs mb-4 flex items-center gap-1.5 ${plan.isPopular ? 'text-slate-400' : 'text-slate-500'}`}>
									<span className='font-bold text-emerald-500'>Support:</span>
									<span>{plan.support}</span>
								</div>

								<button
									onClick={() => navigate(plan.actionUrl)}
									className={`w-full py-3.5 px-6 rounded-xl font-bold text-sm transition-all shadow-sm ${
										plan.isPopular
											? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20'
											: 'bg-slate-900 hover:bg-slate-800 text-white'
									}`}>
									{plan.ctaText} →
								</button>
								<p className='text-center text-[11px] mt-2 text-slate-400'>
									Dispatched in 24 Hours • Free Pan-India Courier
								</p>
							</div>
						</div>
					))}
				</div>

				{/* Bulk Agency / Reseller Box */}
				<div className='mt-14 bg-slate-50 rounded-2xl p-8 border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6'>
					<div>
						<span className='px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700'>
							Bulk & Agency Whitelabel
						</span>
						<h3 className='text-xl sm:text-2xl font-bold text-slate-900 mt-2'>
							Need 20+ Cards, Custom Acrylic Standees or Whitelabel Reseller Portal?
						</h3>
						<p className='text-slate-600 text-sm mt-1 max-w-2xl'>
							We provide full-color CMYK offset printing with your logo, solid acrylic and wooden desktop stands, custom agency portals, and reseller volume pricing.
						</p>
					</div>
					<a
						href='https://wa.me/919880889303?text=Hi%20AlphaReview%2C%20I%20am%20interested%20in%20Bulk%20Cards%20or%20Agency%20Whitelabel'
						target='_blank'
						rel='noopener noreferrer'
						className='px-6 py-3 rounded-xl font-bold text-sm text-white bg-slate-900 hover:bg-slate-800 whitespace-nowrap shadow-sm'>
						Talk To Bulk Team
					</a>
				</div>
			</Container>
		</section>
	);
};

export default Pricing;
